import React, { ChangeEvent, Dispatch, Fragment, SetStateAction, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AlertTriangle, BadgeCheck, Info, Mail, Pencil, Plus, Search, Trash, Upload, User } from 'lucide-react';

import { Checkbox, TextInput } from '../shared/Inputs';
import { Tooltip } from '../shared/Tooltip';
import { ExclusiveMultiFilterSelect } from '../shared/ExclusiveMultiFilterSelect';
import { EntitlementTableCells, EntitlementTableHeaderCells } from '../sub/EntitlementTableCells';

import { openModal } from '../../store/modal.slice';

import { useBulkInviteEmployees, useGetEmployees, useImportEmployeesFromCsv, useInviteEmployee } from '../../hooks/employees-cax';
import { useGetSubClients } from '../../hooks/sub_clients';
import { useGetCohorts } from '../../hooks/cohorts';

import { IEmployeeListItem, TEmployeeEntitlementsView } from '../../types/services.interfaces/employees-cax';

const ENTITLEMENTS_VIEW_OPTIONS: { value: TEmployeeEntitlementsView; label: string }[] = [
    { value: 'client-only', label: 'Client only' },
    { value: 'effective', label: 'Effective' },
];

const isEmployeeSelectableForBulkInvite = (employee: IEmployeeListItem) =>
    !employee.email_verified && employee.invitation_status !== 'accepted';

export const Employees = () => {
    const dispatch = useDispatch();
    const importCsvInputRef = useRef<HTMLInputElement | null>(null);

    const [entitlementsView, setEntitlementsView] = useState<TEmployeeEntitlementsView>('client-only');
    const [isImportingEmployeesFromCsvProcessing, setIsImportingEmployeesFromCsvProcessing] = useState(false);
    const [isBulkInviteProcessing, setIsBulkInviteProcessing] = useState(false);
    const [filteredEmployees, setFilteredEmployees] = useState<IEmployeeListItem[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

    const { data: employees, isLoading } = useGetEmployees(entitlementsView);
    const { mutate: importEmployeesFromCsv } = useImportEmployeesFromCsv();
    const { mutate: bulkInviteEmployees } = useBulkInviteEmployees();

    const handleImportEmployeesFromCsv = (file: File) => {
        importEmployeesFromCsv({
            payload: { file },
            setIsProcessing: setIsImportingEmployeesFromCsvProcessing
        });
    };

    const handleImportEmployeesButtonClick = () => {
        importCsvInputRef.current?.click();
    };

    const handleImportEmployeesCsvSelected = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) {
            return;
        };

        handleImportEmployeesFromCsv(selectedFile);
        event.target.value = '';
    };

    useEffect(() => {
        if (!employees) return;

        const selectableUserIds = new Set(
            employees
                .filter(isEmployeeSelectableForBulkInvite)
                .map(employee => employee.user_id)
        );
        setSelectedUserIds(prev => prev.filter(userId => selectableUserIds.has(userId)));
    }, [employees]);

    const selectableFilteredEmployees = filteredEmployees.filter(isEmployeeSelectableForBulkInvite);
    const selectableFilteredUserIds = selectableFilteredEmployees.map(employee => employee.user_id);
    const allSelectableFilteredSelected = selectableFilteredUserIds.length > 0
        && selectableFilteredUserIds.every(userId => selectedUserIds.includes(userId));

    const toggleEmployeeSelection = (userId: string) => {
        setSelectedUserIds(prev => prev.includes(userId)
            ? prev.filter(id => id !== userId)
            : [...prev, userId]);
    };

    const toggleSelectAllFiltered = () => {
        if (allSelectableFilteredSelected) {
            setSelectedUserIds(prev => prev.filter(userId => !selectableFilteredUserIds.includes(userId)));
        } else {
            setSelectedUserIds(prev => Array.from(new Set([...prev, ...selectableFilteredUserIds])));
        }
    };

    const handleBulkInvite = () => {
        if (!selectedUserIds.length || isBulkInviteProcessing) return;

        dispatch(openModal({
            name: 'CONFIRMATION_MODAL', data: {
                title: 'Bulk Invite Participants?',
                description: `Are you sure you want to invite ${selectedUserIds.length} selected participant${selectedUserIds.length === 1 ? '' : 's'}?`,
                onConfirm: () => {
                    bulkInviteEmployees({
                        payload: { user_ids: selectedUserIds },
                        setIsProcessing: setIsBulkInviteProcessing,
                    }, {
                        onSuccess: () => setSelectedUserIds([]),
                    });
                }
            }
        }));
    };

    return (
        <div className='employees__page'>
            <div className="employees__page__header">
                <p className='text-4xl text-semibold'>Participants</p>
                <div className="employees__page__entitlements_toggle" role="group" aria-label="Entitlements view">
                    {ENTITLEMENTS_VIEW_OPTIONS.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            className={entitlementsView === option.value ? 'active' : ''}
                            onClick={() => setEntitlementsView(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className={`employees__entitlements_note ${entitlementsView === 'effective' ? 'employees__entitlements_note--warning' : ''}`}>
                {entitlementsView === 'effective' ? <AlertTriangle /> : <Info />}
                <p className="text-sm">
                    {entitlementsView === 'client-only'
                        ? 'Showing client-only entitlements. These are grants allocated directly to each participant from the client pool. Sub client and cohort grants are not included.'
                        : 'Showing effective entitlements — the combined grants each participant currently has from the client, sub client, and cohort pools. The manage modal still only lets you edit client-only entitlements. Sub client and cohort grants must be managed from those screens.'}
                </p>
            </div>
            <Filters
                employees={employees}
                setFilteredEmployees={setFilteredEmployees}
                isImportingEmployeesFromCsvProcessing={isImportingEmployeesFromCsvProcessing}
                onImportEmployeesButtonClick={handleImportEmployeesButtonClick}
                onResetSelection={() => setSelectedUserIds([])}
            />
            <input
                ref={importCsvInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleImportEmployeesCsvSelected}
                style={{ display: 'none' }}
            />
            {isLoading && (
                <div className="employees__data__loading">
                    <div className='list-loader' />
                    <p className="text-medium">Loading Participants...</p>
                </div>
            )}
            {!isLoading && (
                <div className="employees__data">
                    {!employees?.length && (
                        <div className="employees__data__empty">
                            <p className="text-medium">No participants yet!</p>
                        </div>
                    )}
                    {!!employees?.length && (
                        <Fragment>
                            {!filteredEmployees.length && (
                                <div className="employees__data__empty">
                                    <p className="text-medium">No participants found.</p>
                                </div>
                            )}
                            {!!filteredEmployees.length && (
                                <div className="employees__data__table_wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th className="employees__data__table__select_cell">
                                                    <Checkbox
                                                        checked={allSelectableFilteredSelected}
                                                        label=""
                                                        disabled={!selectableFilteredUserIds.length}
                                                        onChange={toggleSelectAllFiltered}
                                                    />
                                                </th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-left">Name</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-left">Email</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-left">Sub Client</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-center">Cohorts</p></th>
                                                <EntitlementTableHeaderCells />
                                                <th><p className="text-xs text-medium color--txt-disabled text-center">Invitation Status</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-center">Action</p></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredEmployees.map((employee) => (
                                                <EmployeeRow
                                                    key={employee.id}
                                                    employee={employee}
                                                    isSelected={selectedUserIds.includes(employee.user_id)}
                                                    isSelectDisabled={!isEmployeeSelectableForBulkInvite(employee)}
                                                    onToggleSelect={toggleEmployeeSelection}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Fragment>
                    )}
                    <div className="employees__data__actions">
                        <button
                            className='btn btn--sm btn--radius-sm btn--secondary'
                            onClick={handleBulkInvite}
                            disabled={!selectedUserIds.length || isBulkInviteProcessing}
                        >
                            <Mail />
                            <span>{isBulkInviteProcessing ? 'Sending Invites...' : 'Bulk invite'}</span>
                            {isBulkInviteProcessing && <div className='btn-loader' />}
                        </button>
                        <button
                            className='btn btn--sm btn--radius-sm btn--primary'
                            onClick={() => dispatch(openModal({ name: 'MANAGE_EMPLOYEE_MODAL', data: { employeeId: null } }))}
                        >
                            <Plus />
                            <span>Add Participant</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

interface IFiltersProps {
    employees: IEmployeeListItem[] | undefined;
    setFilteredEmployees: Dispatch<SetStateAction<IEmployeeListItem[]>>;
    isImportingEmployeesFromCsvProcessing: boolean;
    onImportEmployeesButtonClick: () => void;
    onResetSelection: () => void;
};
const Filters = (props: IFiltersProps) => {
    const {
        employees,
        setFilteredEmployees,
        isImportingEmployeesFromCsvProcessing,
        onImportEmployeesButtonClick,
        onResetSelection,
    } = props;

    const hasInitializedSubClientFilter = useRef(false);
    const hasInitializedCohortFilter = useRef(false);

    const [filterInputValue, setFilterInputValue] = useState<string>('');
    const [isSubClientFilterReady, setIsSubClientFilterReady] = useState<boolean>(false);
    const [subClientFilter, setSubClientFilter] = useState<{ includeNone: boolean; selectedIds: string[] }>({ includeNone: true, selectedIds: [] });
    const [isCohortFilterReady, setIsCohortFilterReady] = useState<boolean>(false);
    const [cohortFilter, setCohortFilter] = useState<{ includeNone: boolean; selectedIds: string[] }>({ includeNone: true, selectedIds: [] });

    const { data: subClients } = useGetSubClients(true);
    const { data: cohorts } = useGetCohorts(true);

    useEffect(() => {
        if (!subClients || hasInitializedSubClientFilter.current) return;

        hasInitializedSubClientFilter.current = true;
        setSubClientFilter({
            includeNone: true,
            selectedIds: subClients.map(subClient => subClient.id.toString()),
        });
        setIsSubClientFilterReady(true);
    }, [subClients]);

    useEffect(() => {
        if (!cohorts || hasInitializedCohortFilter.current) return;

        hasInitializedCohortFilter.current = true;
        setCohortFilter({
            includeNone: true,
            selectedIds: cohorts.map(cohort => cohort.id.toString()),
        });
        setIsCohortFilterReady(true);
    }, [cohorts]);

    useEffect(() => {
        if (!employees) return;

        const searchValue = filterInputValue.toLowerCase();
        const isAllSubClientsSelected = !!subClients?.length
            && subClients.every(subClient => subClientFilter.selectedIds.includes(subClient.id.toString()));
        const isAllCohortsSelected = !!cohorts?.length
            && cohorts.every(cohort => cohortFilter.selectedIds.includes(cohort.id.toString()));

        setFilteredEmployees(employees.filter(employee => {
            const matchesSearch = !searchValue
                || employee.first_name?.toLowerCase().includes(searchValue)
                || employee.last_name?.toLowerCase().includes(searchValue)
                || employee.email?.toLowerCase().includes(searchValue)
                || employee.sub_client?.name?.toLowerCase().includes(searchValue)
                || employee.cohorts.some(cohort => cohort.name?.toLowerCase().includes(searchValue));

            const matchesSubClient = !isSubClientFilterReady
                ? true
                : subClientFilter.includeNone && (isAllSubClientsSelected || !subClients?.length)
                    ? true
                    : subClientFilter.includeNone
                        ? !employee.sub_client
                        : subClientFilter.selectedIds.includes(employee.sub_client?.id.toString() ?? '');

            const matchesCohort = !isCohortFilterReady
                ? true
                : cohortFilter.includeNone && (isAllCohortsSelected || !cohorts?.length)
                    ? true
                    : cohortFilter.includeNone
                        ? !employee.cohorts.length
                        : employee.cohorts.some(cohort => cohortFilter.selectedIds.includes(cohort.id.toString()));

            return matchesSearch && matchesSubClient && matchesCohort;
        }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employees, filterInputValue, isSubClientFilterReady, subClientFilter, isCohortFilterReady, cohortFilter, subClients, cohorts]);

    const handleFilter = (e: ChangeEvent<HTMLInputElement>) => {
        setFilterInputValue(e.target.value);
        onResetSelection();
    };

    return (
        <div className="employees__filters">
            <div className="employees__filters__left">
                <TextInput
                    icon={{ icon: <Search />, position: 'prefix' }}
                    placeholder="Search participants"
                    value={filterInputValue}
                    onChange={handleFilter}
                    disabled={!employees?.length}
                />
                <ExclusiveMultiFilterSelect
                    noneLabel="No Sub Client"
                    groupAllLabel="All Sub Clients"
                    items={(subClients ?? []).map(subClient => ({ id: subClient.id.toString(), name: subClient.name }))}
                    includeNone={subClientFilter.includeNone}
                    selectedIds={subClientFilter.selectedIds}
                    disabled={!employees?.length}
                    onChange={(next) => {
                        setSubClientFilter(next);
                        onResetSelection();
                    }}
                />
                <ExclusiveMultiFilterSelect
                    noneLabel="No Cohort"
                    groupAllLabel="All Cohorts"
                    items={(cohorts ?? []).map(cohort => ({ id: cohort.id.toString(), name: cohort.name }))}
                    includeNone={cohortFilter.includeNone}
                    selectedIds={cohortFilter.selectedIds}
                    disabled={!employees?.length}
                    onChange={(next) => {
                        setCohortFilter(next);
                        onResetSelection();
                    }}
                />
            </div>
            <button
                className='btn btn--sm btn--radius-sm btn--secondary'
                onClick={onImportEmployeesButtonClick}
                disabled={isImportingEmployeesFromCsvProcessing}
            >
                <Upload />
                <span>{isImportingEmployeesFromCsvProcessing ? 'Importing Participants...' : 'Import Participants'}</span>
                {isImportingEmployeesFromCsvProcessing && <div className='btn-loader' />}
            </button>
        </div>
    );
};

interface IEmployeeRowProps {
    employee: IEmployeeListItem;
    isSelected: boolean;
    isSelectDisabled: boolean;
    onToggleSelect: (userId: string) => void;
};
const EmployeeRow = ({ employee, isSelected, isSelectDisabled, onToggleSelect }: IEmployeeRowProps) => {
    const dispatch = useDispatch();

    const [isProcessing, setIsProcessing] = useState(false);

    const { mutate: inviteEmployee } = useInviteEmployee();

    const handleInviteEmployee = (employee: IEmployeeListItem) => {
        if (isProcessing || employee.email_verified || employee.invitation_status === 'accepted') return;

        dispatch(openModal({
            name: 'CONFIRMATION_MODAL', data: {
                title: employee.invitation_status === 'not-invited' ? 'Invite Participant?' : 'Resend Invite?',
                description: employee.invitation_status === 'not-invited'
                    ? `Are you sure you want to invite the participant "${employee.first_name} ${employee.last_name}"?`
                    : `Are you sure you want to resend the invite to the participant "${employee.first_name} ${employee.last_name}"?`,
                onConfirm: () => {
                    inviteEmployee({
                        userId: employee.user_id,
                        setIsProcessing,
                    });
                }
            }
        }));
    };

    return (
        <tr>
            <td className="employees__data__table__select_cell">
                <Checkbox
                    checked={isSelected}
                    label=""
                    disabled={isSelectDisabled}
                    onChange={() => onToggleSelect(employee.user_id)}
                />
            </td>
            <td>
                <div className='employees__data__table__name_cell'>
                    <div>
                        {(employee.first_name || employee.last_name)
                            ? <p>{employee.first_name?.charAt(0)?.toUpperCase() ?? '-'}{employee.last_name?.charAt(0)?.toUpperCase() ?? '-'}</p>
                            : <User />
                        }
                    </div>
                    <p className="text-sm text-medium">{employee.first_name} {employee.last_name}</p>
                </div>
            </td>
            <td><p className="text-sm color--txt-muted">{employee.email ?? '-'}</p></td>
            <td><p className="text-sm color--txt-muted">{employee.sub_client?.name ?? '-'}</p></td>
            <td>
                <p className="text-sm color--txt-muted text-center">
                    {employee.cohorts.length ? (
                        employee.cohorts.map((cohort, index) => `${cohort.name}${index < employee.cohorts.length - 1 ? ', ' : ''}`)
                    ) : (
                        '-'
                    )}
                </p>
            </td>
            <EntitlementTableCells entitlements={employee.entitlements} />
            <td>
                <div className='employees__data__table__invitation_status_cell'>
                    {employee.email_verified && (
                        <div className='employees__data__table__invitation_status_cell__status_pill employees__data__table__invitation_status_cell__status_pill--verified'>
                            <BadgeCheck />
                        </div>
                    )}
                    {!employee.email_verified && (
                        <Fragment>
                            {employee.invitation_status !== 'not-invited' && (
                                <div className={`employees__data__table__invitation_status_cell__status_pill employees__data__table__invitation_status_cell__status_pill--${employee.invitation_status}`}>
                                    <p>{employee.invitation_status.charAt(0).toUpperCase() + employee.invitation_status.slice(1)}</p>
                                </div>
                            )}
                            {employee.invitation_status !== 'accepted' && (
                                <button
                                    className='link link--primary text-xs text-medium'
                                    onClick={() => handleInviteEmployee(employee)}
                                >
                                    {isProcessing ? 'Sending Invite...' : employee.invitation_status === 'not-invited' ? 'Invite' : 'Resend Invite'}
                                    {isProcessing && <div className='btn-loader' />}
                                </button>
                            )}
                        </Fragment>
                    )}
                </div>
            </td>
            <td>
                <div className='employees__data__table__actions_cell'>
                    <Tooltip content="Manage participant & entitlements">
                        <button
                            className="btn btn--icon-sm btn--radius-sm btn--secondary"
                            onClick={() => dispatch(openModal({ name: 'MANAGE_EMPLOYEE_MODAL', data: { employeeId: employee.user_id } }))}
                        >
                            <Pencil />
                        </button>
                    </Tooltip>
                    <Tooltip content="Delete participant (Not available)">
                        <button
                            className="btn btn--icon-sm btn--radius-sm btn--secondary" disabled>
                            <Trash />
                        </button>
                    </Tooltip>
                </div>
            </td>
        </tr>
    );
};
